import { useState, useEffect } from 'react';
import type { ChangeEvent, ElementType, InputHTMLAttributes, ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { cnpj as cnpjValidator, cpf as cpfValidator } from 'cpf-cnpj-validator';
import { invitesService } from '@/services/invites.service';
import { cn } from '@/lib/utils';

// Icons (Lucide React)
import {
    Building2, User, CheckCircle2,
    MapPin, Loader2, AlertCircle,
    Copy, ExternalLink, TrendingUp, Shield, Users as UsersIcon, Check,
    Sparkles, ChevronRight, ChevronLeft, Eye, EyeOff
} from 'lucide-react';

// ----------------------------------------------------------------------------
// Components: UI Primitives (Enterprise Sharp Design - Dark Theme)
// ----------------------------------------------------------------------------

const CardSelection = ({
    selected,
    onClick,
    icon: Icon,
    title,
    description
}: {
    selected: boolean;
    onClick: () => void;
    icon: ElementType;
    title: string;
    description: string;
}) => (
    <button
        onClick={onClick}
        type="button"
        className={cn(
            "relative group flex flex-col items-center p-8 text-center transition-all duration-300 border-2 w-full rounded-2xl",
            selected
                ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_30px_-10px_rgba(16,185,129,0.4)]'
                : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700 hover:bg-zinc-900'
        )}
    >
        <div className={cn(
            "p-4 mb-4 rounded-xl transition-all duration-300",
            selected ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-400'
        )}>
            <Icon className="w-8 h-8" />
        </div>
        <h3 className={cn("font-bold text-lg mb-2 transition-colors", selected ? 'text-white' : 'text-zinc-300')}>
            {title}
        </h3>
        <p className="text-sm text-zinc-500">
            {description}
        </p>

        {/* Checkmark indicator */}
        {selected && (
            <div className="absolute top-4 right-4 text-emerald-500">
                <CheckCircle2 className="w-6 h-6" />
            </div>
        )}
    </button>
);

const Input = ({
    label,
    error,
    className = "",
    icon,
    ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; icon?: ReactNode }) => (
    <div className={cn("group relative space-y-2", className)}>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 transition-colors group-focus-within:text-emerald-400">
            {label} {props.required && <span className="text-emerald-500">*</span>}
        </label>
        <div className="relative">
            <input
                className={cn(
                    "w-full bg-zinc-900/60 border border-zinc-800 text-white px-5 py-4 rounded-xl",
                    "focus:outline-none focus:border-emerald-500/50 focus:bg-zinc-900/60 transition-all duration-300",
                    "placeholder:text-zinc-600 font-medium text-base tracking-wide shadow-sm",
                    icon ? "pl-12" : "",
                    error ? "border-red-500/50 focus:border-red-500" : ""
                )}
                {...props}
            />
            {icon && (
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 transition-colors group-focus-within:text-emerald-400">
                    {icon}
                </div>
            )}
        </div>
        {error && (
            <span className="text-xs text-red-400 font-medium flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" /> {error}
            </span>
        )}
    </div>
);

const Checkbox = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
    <div
        onClick={() => onChange(!checked)}
        className="flex items-center gap-4 cursor-pointer group"
    >
        <div className={cn(
            "w-6 h-6 border-2 rounded-lg flex items-center justify-center transition-all",
            checked ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-600 bg-zinc-900 group-hover:border-zinc-500'
        )}>
            {checked && <CheckCircle2 className="w-4 h-4 text-black" />}
        </div>
        <span className="text-zinc-300 font-medium">{label}</span>
    </div>
);

// ----------------------------------------------------------------------------
// Main Page: 4-Step Wizard (Restored Layout + New Logic)
// ----------------------------------------------------------------------------

export function PublicRegistrationPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const inviteToken = searchParams.get('token');
    const b2bRef = searchParams.get('b2b_ref');

    // Token Validation
    const [isValidating, setIsValidating] = useState(true);
    const [inviteData, setInviteData] = useState<{ email?: string; plan?: any; inviterName?: string } | null>(null);
    const [tokenError, setTokenError] = useState<string | null>(null);

    // Wizard State
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [contractLink, setContractLink] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        // Step 1: Type
        tenant_type: 'PJ' as 'PJ' | 'PF',

        // Step 2: Entity Data
        name: '',
        company_name: '',
        trade_name: '',
        cnpj: '',
        cpf: '',
        full_name: '',
        email: '',
        phone: '',

        // Address
        zip_code: '',
        street: '',
        number: '',
        neighborhood: '',
        city: '',
        state: '',

        // Step 3: Admin User
        admin_email: '',
        admin_password: '',

        // Step 4: Config
        generate_contract: true
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Validate Token on Mount
    useEffect(() => {
        async function validate() {
            if (!inviteToken && !b2bRef) {
                setTokenError('Token de convite não encontrado na URL.');
                setIsValidating(false);
                return;
            }

            try {
                if (b2bRef) {
                    const result = await invitesService.validateTenantReferral(b2bRef);
                    if (result.valid) {
                        setInviteData({ inviterName: result.inviter_name });
                    } else {
                        setTokenError('Este link de indicação é inválido.');
                    }
                    return;
                }

                const result = await invitesService.validateToken(inviteToken as string);
                if (result.valid) {
                    setInviteData({ email: result.email, plan: result.plan_details });
                    setFormData(prev => ({ ...prev, admin_email: result.email }));
                } else {
                    setTokenError('Este convite é inválido ou expirou.');
                }
            } catch (err: any) {
                setTokenError(err.message || 'Erro ao validar o convite.');
            } finally {
                setIsValidating(false);
            }
        }
        validate();
    }, [inviteToken, b2bRef]);

    // Helpers
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleMask = (e: ChangeEvent<HTMLInputElement>, mask: 'cpf' | 'cnpj' | 'cep' | 'phone') => {
        let v = e.target.value.replace(/\D/g, '');

        if (mask === 'cpf') {
            v = v.replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else if (mask === 'cnpj') {
            v = v.replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2');
        } else if (mask === 'cep') {
            v = v.replace(/^(\d{5})(\d)/, '$1-$2');
        } else if (mask === 'phone') {
            v = v.replace(/^(\d{2})(\d)/g, '($1) $2')
                .replace(/(\d)(\d{4})$/, '$1-$2');
        }

        handleChange({ target: { name: e.target.name, value: v } } as any);
    };

    // CEP Search
    const handleCepBlur = async () => {
        const cep = formData.zip_code.replace(/\D/g, '');
        if (cep.length !== 8) return;

        try {
            setLoading(true);
            const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await res.json();

            if (!data.erro) {
                setFormData(prev => ({
                    ...prev,
                    street: data.logradouro,
                    neighborhood: data.bairro,
                    city: data.localidade,
                    state: data.uf
                }));
                toast.success('Endereço encontrado!');
            } else {
                toast.error('CEP não encontrado');
            }
        } catch {
            toast.error('Erro ao buscar CEP');
        } finally {
            setLoading(false);
        }
    };

    // Validation Logic
    const validateStep = (currentStep: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (currentStep === 2) {
            if (!formData.name || formData.name.length < 3) newErrors.name = 'Nome do sistema inválido (min 3 chars).';
            if (!formData.email) newErrors.email = 'Email comercial obrigatório.';

            if (formData.tenant_type === 'PJ') {
                if (!formData.company_name) newErrors.company_name = 'Razão Social obrigatória.';
                if (!cnpjValidator.isValid(formData.cnpj)) newErrors.cnpj = 'CNPJ inválido (verifique os dígitos).';
            } else {
                if (!formData.full_name) newErrors.full_name = 'Nome completo obrigatório.';
                if (!cpfValidator.isValid(formData.cpf)) newErrors.cpf = 'CPF inválido (verifique os dígitos).';
            }

            if (!formData.zip_code) newErrors.zip_code = 'CEP obrigatório.';
            if (!formData.number) newErrors.number = 'Número obrigatório.';
            if (!formData.street) newErrors.street = 'Preencha o endereço.';
        }

        if (currentStep === 3) {
            if (!formData.admin_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
                newErrors.admin_email = 'Email de login inválido.';
            }
            if (!formData.admin_password || formData.admin_password.length < 6) {
                newErrors.admin_password = 'Senha deve ter no mínimo 6 caracteres.';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Verifique os campos obrigatórios.');
            return false;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
        }
    };

    const prevStep = () => setStep(prev => prev - 1);

    // Password Generator
    const generatePassword = () => {
        const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
        let pass = "";
        for (let i = 0; i < 12; i++) {
            pass += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, admin_password: pass }));
        navigator.clipboard.writeText(pass);
        toast.success('Senha gerada e copiada!');
    };

    // Submit Handler
    const handleSubmit = async () => {
        if (!inviteToken && !b2bRef) return;

        setLoading(true);
        try {
            const payload = {
                ...(inviteToken ? { token: inviteToken } : { b2b_ref: b2bRef as string }),
                name: formData.trade_name || formData.name,
                tenant_type: formData.tenant_type,
                email: formData.email,
                admin_username: formData.admin_email,
                admin_email: formData.admin_email,
                admin_password: formData.admin_password,
                ...(formData.tenant_type === 'PJ' ? {
                    cnpj: formData.cnpj,
                    company_name: formData.company_name,
                    trade_name: formData.trade_name
                } : {
                    cpf: formData.cpf,
                    full_name: formData.full_name
                }),
                zip_code: formData.zip_code,
                street: formData.street,
                number: formData.number,
                neighborhood: formData.neighborhood,
                city: formData.city,
                state: formData.state
            };

            const result = await invitesService.registerTenant(payload);
            toast.success('Cadastro realizado com sucesso!');

            if (result.redirect_url) {
                setContractLink(result.redirect_url);
            } else {
                navigate('/login');
            }

        } catch (error: any) {
            if (error.message?.includes('nome')) {
                setErrors({ name: 'Este nome já está em uso.' });
                setStep(2);
            } else if (error.message?.includes('CPF') || error.message?.includes('CNPJ')) {
                setErrors({ [formData.tenant_type === 'PJ' ? 'cnpj' : 'cpf']: 'Documento já cadastrado.' });
                setStep(2);
            }
            toast.error(error.message || 'Erro ao criar cadastro');
        } finally {
            setLoading(false);
        }
    };

    // Loading State
    if (isValidating) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                <p className="text-zinc-500 font-medium tracking-wide animate-pulse">VALIDANDO CONVITE...</p>
            </div>
        );
    }

    // Error State
    if (tokenError) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-zinc-900 border border-red-900/50 p-8 rounded-2xl text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
                    <h2 className="text-2xl font-bold text-white mb-2">Convite Inválido</h2>
                    <p className="text-zinc-400 mb-8">{tokenError}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full py-4 rounded-xl bg-zinc-800 text-white font-medium hover:bg-zinc-700 transition-colors"
                    >
                        Voltar para Login
                    </button>
                </div>
            </div>
        );
    }

    // Success State (Contract Link)
    if (contractLink) {
        const fullLink = `${window.location.origin}${contractLink}`;
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-6 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-black to-black">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-lg w-full bg-zinc-900 border border-emerald-500/30 p-10 rounded-3xl text-center shadow-[0_0_60px_-20px_rgba(16,185,129,0.3)]"
                >
                    <div className="mb-8 flex justify-center">
                        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-3">Cadastro Concluído!</h2>
                    <p className="text-zinc-400 mb-8 text-lg">
                        Para ativar sua conta, assine o contrato de adesão clicando no link abaixo.
                    </p>

                    <div className="bg-zinc-800/50 p-4 rounded-xl border border-zinc-700 mb-6">
                        <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider font-bold">Link do Contrato</p>
                        <p className="text-emerald-400 font-mono text-sm break-all">{fullLink}</p>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={() => window.location.href = contractLink}
                            className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                            <ExternalLink className="w-5 h-5" />
                            Ir para Assinatura
                        </button>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(fullLink);
                                toast.success('Link copiado!');
                            }}
                            className="w-full py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                            <Copy className="w-5 h-5" />
                            Copiar Link
                        </button>
                    </div>
                </motion.div>
            </div>
        );
    }

    const STEPS = [
        { num: 1, label: 'Identificação', subtitle: 'Tipo de Conta' },
        { num: 2, label: 'Dados', subtitle: 'Informações' },
        { num: 3, label: 'Acesso', subtitle: 'Administrador' },
        { num: 4, label: 'Confirmação', subtitle: 'Contrato' },
    ];

    return (
        <div className="min-h-screen bg-black flex flex-col lg:flex-row overflow-hidden font-sans selection:bg-emerald-500/30">
            {/* Left Column: Narrative (50%) - RESTORED MARKETING SIDEBAR */}
            <div className="lg:w-1/2 relative hidden lg:flex flex-col justify-center items-end p-12 custom-scrollbar overflow-y-auto border-r border-zinc-900 bg-zinc-950">
                {/* Background Texture */}
                <div className="absolute inset-0 bg-[#0A0A0A] -z-10" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />

                <div className="max-w-lg w-full mr-8 xl:mr-16">
                    {/* Brand */}
                    <div className="flex items-center gap-3 mb-16">
                        <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
                            <Building2 className="text-black font-bold w-6 h-6" />
                        </div>
                        <span className="text-xl font-bold text-white tracking-tight">QuadraDois</span>
                    </div>

                    {/* Headline */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-12"
                    >
                        <h1 className="text-5xl font-bold text-white leading-[1.1] mb-6">
                            Finalize seu <br />
                            <span className="text-emerald-500">Cadastro</span>
                        </h1>
                        <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
                            {inviteData?.inviterName
                                ? `Você foi indicado por ${inviteData.inviterName} para entrar na Parceria Quadra Dois.`
                                : `Você foi convidado para criar uma conta imobiliária${inviteData?.plan?.plan_name ? ` no plano ${inviteData.plan.plan_name}` : ''}.`}
                        </p>
                    </motion.div>

                    {/* Value Props - RESTORED */}
                    <div className="space-y-8 max-w-sm">
                        {[
                            { icon: TrendingUp, title: "Gestão Completa", desc: "Controle total da sua carteira." },
                            { icon: Shield, title: "Segurança de Dados", desc: "Criptografia de ponta a ponta." },
                            { icon: UsersIcon, title: "Multiusuário", desc: "Gerencie seu time com eficiência." }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 + (idx * 0.1) }}
                                className="flex items-start gap-4"
                            >
                                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800 text-emerald-500">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-zinc-200">{item.title}</h3>
                                    <p className="text-sm text-zinc-500">{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-sm text-zinc-600 mt-24">
                        © 2024 QuadraDois. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Right Column: Wizard (50%) - STEPPER MOVED TO TOP */}
            <div className="flex-1 lg:w-1/2 bg-black lg:bg-[#050505] flex flex-col relative overflow-hidden">
                {/* Mobile Header */}
                <div className="lg:hidden p-6 border-b border-zinc-900 flex items-center justify-between bg-zinc-950/80 backdrop-blur-md sticky top-0 z-50">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                            <Building2 className="text-black w-5 h-5" />
                        </div>
                        <span className="font-bold text-white">QuadraDois</span>
                    </div>
                    <div className="text-right">
                        <span className="text-xs text-zinc-500 uppercase font-bold">Passo</span>
                        <div className="text-emerald-500 font-mono font-bold">{step}/4</div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-8 lg:py-16 flex flex-col lg:justify-center">
                    <div className="max-w-lg w-full mx-auto lg:mx-0 lg:ml-8 xl:ml-16 pt-4 lg:pt-0 pb-32">

                        {/* Desktop Horizontal Stepper - RESTORED/ADAPTED */}
                        <div className="hidden lg:flex items-center justify-between mb-16 relative">
                            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-zinc-900 -z-10" />
                            <div
                                className="absolute top-1/2 left-0 h-0.5 bg-emerald-500 transition-all duration-500 -z-10"
                                style={{ width: `${((step - 1) / 3) * 100}%` }}
                            />
                            {STEPS.map((s) => {
                                const active = s.num <= step;
                                const current = s.num === step;

                                return (
                                    <div key={s.num} className="flex flex-col items-center gap-2 bg-black px-2">
                                        <div className={cn(
                                            "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 font-mono font-bold text-sm",
                                            active ? "border-emerald-500 bg-emerald-500 text-black" : "border-zinc-800 bg-zinc-900 text-zinc-600",
                                            current && "ring-4 ring-emerald-500/20 scale-110"
                                        )}>
                                            {active && !current ? <Check className="w-5 h-5" /> : s.num}
                                        </div>
                                        <div className="text-center">
                                            <div className={cn("text-xs font-bold uppercase tracking-wider transition-colors", active ? "text-white" : "text-zinc-600")}>
                                                {s.label}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Form Title */}
                        <div className="mb-10">
                            <h2 className="text-4xl font-bold text-white mb-3 tracking-tight">{STEPS[step - 1]?.label}</h2>
                            <p className="text-zinc-400 text-lg">{STEPS[step - 1]?.subtitle}</p>
                        </div>

                        {/* Form Content */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-8"
                            >
                                {/* STEP 1: TYPE */}
                                {step === 1 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <CardSelection
                                            title="Pessoa Jurídica"
                                            description="Imobiliárias com CNPJ, equipes e múltiplos corretores."
                                            icon={Building2}
                                            selected={formData.tenant_type === 'PJ'}
                                            onClick={() => setFormData(prev => ({ ...prev, tenant_type: 'PJ' }))}
                                        />
                                        <CardSelection
                                            title="Corretor Autônomo"
                                            description="Profissionais independentes com CPF."
                                            icon={User}
                                            selected={formData.tenant_type === 'PF'}
                                            onClick={() => setFormData(prev => ({ ...prev, tenant_type: 'PF' }))}
                                        />
                                    </div>
                                )}

                                {/* STEP 2: DATA */}
                                {step === 2 && (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <Input
                                                label="Nome do Sistema (Slug)"
                                                name="name"
                                                value={formData.name}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
                                                    setFormData(prev => ({ ...prev, name: val }));
                                                    if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
                                                }}
                                                placeholder="ex: imobiliaria-modelo"
                                                error={errors.name}
                                                required
                                            />
                                            <Input
                                                label="Email Comercial"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                error={errors.email}
                                                required
                                                icon={<CheckCircle2 className="w-4 h-4" />} // Just a placeholder icon usage
                                            />

                                            {formData.tenant_type === 'PJ' ? (
                                                <>
                                                    <Input
                                                        label="CNPJ"
                                                        name="cnpj"
                                                        maxLength={18}
                                                        value={formData.cnpj}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleMask(e, 'cnpj')}
                                                        error={errors.cnpj}
                                                        placeholder="00.000.000/0000-00"
                                                        required
                                                    />
                                                    <Input
                                                        label="Razão Social"
                                                        name="company_name"
                                                        value={formData.company_name}
                                                        onChange={handleChange}
                                                        error={errors.company_name}
                                                        required
                                                    />
                                                    <Input
                                                        label="Nome Fantasia"
                                                        name="trade_name"
                                                        value={formData.trade_name}
                                                        onChange={handleChange}
                                                        className="md:col-span-2"
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <Input
                                                        label="CPF"
                                                        name="cpf"
                                                        maxLength={14}
                                                        value={formData.cpf}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleMask(e, 'cpf')}
                                                        error={errors.cpf}
                                                        placeholder="000.000.000-00"
                                                        required
                                                    />
                                                    <Input
                                                        label="Nome Completo"
                                                        name="full_name"
                                                        value={formData.full_name}
                                                        onChange={handleChange}
                                                        error={errors.full_name}
                                                        required
                                                    />
                                                </>
                                            )}
                                        </div>

                                        <div className="pt-6 border-t border-zinc-800">
                                            <div className="flex items-center gap-3 mb-6">
                                                <MapPin className="w-5 h-5 text-emerald-500" />
                                                <h3 className="font-bold text-white uppercase text-sm tracking-wider">Endereço (Obrigatório)</h3>
                                            </div>
                                            <div className="grid grid-cols-4 gap-4">
                                                <div className="col-span-1 relative">
                                                    <Input
                                                        label="CEP"
                                                        name="zip_code"
                                                        value={formData.zip_code}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleMask(e, 'cep')}
                                                        onBlur={handleCepBlur}
                                                        error={errors.zip_code}
                                                        placeholder="00000-000"
                                                        required
                                                    />
                                                    {loading && <Loader2 className="absolute right-3 top-9 w-4 h-4 animate-spin text-emerald-500" />}
                                                </div>
                                                <div className="col-span-3">
                                                    <Input label="Rua" name="street" value={formData.street} onChange={handleChange} error={errors.street} readOnly />
                                                </div>
                                                <div className="col-span-1">
                                                    <Input label="Número" name="number" value={formData.number} onChange={handleChange} error={errors.number} required />
                                                </div>
                                                <div className="col-span-2">
                                                    <Input label="Bairro" name="neighborhood" value={formData.neighborhood} onChange={handleChange} readOnly />
                                                </div>
                                                <div className="col-span-1">
                                                    <Input label="Estado" name="state" value={formData.state} onChange={handleChange} readOnly />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* STEP 3: ADMIN */}
                                {step === 3 && (
                                    <>
                                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-start gap-3">
                                            <Sparkles className="w-5 h-5 text-emerald-500 mt-0.5" />
                                            <p className="text-sm text-emerald-200">
                                                {inviteData?.email ? (
                                                    <>O email <strong>{inviteData.email}</strong> será usado como login.</>
                                                ) : (
                                                    <>Informe o email que será usado como login do administrador.</>
                                                )}
                                            </p>
                                        </div>

                                        <div className="space-y-6 max-w-md">
                                            <Input
                                                label="Email de Login"
                                                name="admin_email"
                                                type="email"
                                                value={formData.admin_email}
                                                onChange={handleChange}
                                                error={errors.admin_email}
                                                required
                                            />

                                            <div className="space-y-2">
                                                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500">
                                                    Senha de Acesso <span className="text-emerald-500">*</span>
                                                </label>
                                                <div className="flex gap-3">
                                                    <div className="relative flex-1">
                                                        <input
                                                            type={showPassword ? 'text' : 'password'}
                                                            name="admin_password"
                                                            value={formData.admin_password}
                                                            onChange={handleChange}
                                                            autoComplete="new-password"
                                                            className={`w-full p-4 pr-12 bg-zinc-900/60 border-2 rounded-xl font-mono text-white ${errors.admin_password ? 'border-red-500/50' : 'border-zinc-800 focus:border-emerald-500/50'}`}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowPassword(s => !s)}
                                                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                                                        >
                                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={generatePassword}
                                                        className="px-6 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm uppercase tracking-wider rounded-xl transition-colors"
                                                    >
                                                        Gerar
                                                    </button>
                                                </div>
                                                {errors.admin_password && (
                                                    <span className="text-xs text-red-400 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" /> {errors.admin_password}
                                                    </span>
                                                )}
                                                <p className="text-xs text-zinc-600">Clique em "Gerar" para criar uma senha forte automaticamente.</p>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* STEP 4: CONFIRM */}
                                {step === 4 && (
                                    <>
                                        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
                                            {[
                                                { label: 'Tipo', value: formData.tenant_type === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física' },
                                                { label: 'Nome (Sistema)', value: formData.name },
                                                { label: 'Entidade', value: formData.tenant_type === 'PJ' ? formData.company_name : formData.full_name },
                                                { label: 'Documento', value: formData.tenant_type === 'PJ' ? formData.cnpj : formData.cpf },
                                                { label: 'Endereço', value: `${formData.street}, ${formData.number} - ${formData.city}/${formData.state}` },
                                                { label: 'Admin', value: formData.admin_email },
                                            ].map((item, idx) => (
                                                <div key={idx} className="flex justify-between border-b border-zinc-800 pb-3 last:border-0">
                                                    <span className="text-zinc-500">{item.label}:</span>
                                                    <span className="font-medium text-white">{item.value || '-'}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
                                            <Checkbox
                                                checked={formData.generate_contract}
                                                onChange={(c) => setFormData(prev => ({ ...prev, generate_contract: c }))}
                                                label="Gerar Contrato de Adesão Automático"
                                            />
                                            <p className="text-xs text-zinc-500 mt-2 ml-10">
                                                Ao finalizar, um link de assinatura digital será gerado para ativação da conta.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Footer Actions */}
                    <div className="fixed bottom-0 right-0 w-full lg:w-1/2 bg-zinc-950/90 backdrop-blur-xl border-t border-zinc-900 p-6 lg:px-8 xl:px-16 z-10 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={prevStep}
                            disabled={step === 1}
                            className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-colors ${step === 1 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Voltar
                        </button>

                        <button
                            onClick={step < 4 ? nextStep : handleSubmit}
                            disabled={loading}
                            className="px-8 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 flex items-center gap-2"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : step < 4 ? (
                                <>
                                    Continuar
                                    <ChevronRight className="w-4 h-4" />
                                </>
                            ) : (
                                <>Criar & Gerar Contrato</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PublicRegistrationPage;
